import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowRight, History, TrendingUp, MessageSquare, Sparkles, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/Skeleton";
import { EmptyState } from "@/components/EmptyState";
import { SessionCard } from "@/components/SessionCard";
import { Sparkline } from "@/components/Sparkline";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const RECENT_LIMIT = 3;

export default function HomePage() {
    const { user } = useAuth();
    const firstName = user?.full_name?.split(" ")[0] || "there";
    useDocumentTitle("Home");

    const [stats, setStats] = useState(null);
    const [recent, setRecent] = useState(null);

    // Phase 14 polish: admins don't have a "home" — bounce them straight to /admin.
    // Placed after hooks to respect the rules of hooks (don't conditionally call hooks).
    const shouldRedirectAdmin = user?.role === "admin";

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            api.get("/me/stats"),
            api.get("/sessions/mine", { params: { page: 1, page_size: RECENT_LIMIT } }),
        ])
            .then(([s, r]) => {
                if (cancelled) return;
                setStats(s.data);
                setRecent(r.data.items || []);
            })
            .catch(() => {
                if (cancelled) return;
                setStats({});
                setRecent([]);
            });
        return () => { cancelled = true; };
    }, []);

    if (shouldRedirectAdmin) {
        return <Navigate to="/admin" replace />;
    }

    return (
        <Layout>
            <div className="space-y-10 animate-fade-in-up" data-testid="home-page">
                {/* Hero */}
                <section>
                    <p className="text-sm font-medium text-primary">Welcome back</p>
                    <h1 className="mt-1 text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
                        Hi, {firstName}.
                    </h1>
                    <p className="mt-3 max-w-xl text-base text-muted-foreground">
                        Ready for a quiet hour of practice? Pick a client, run a session, and get an
                        honest supervisor review afterwards.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-3">
                        <Button
                            asChild
                            size="lg"
                            className="bg-primary hover:bg-primary-hover"
                            data-testid="home-start-practice-button"
                        >
                            <Link to="/practice/setup">
                                Start practice
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </Button>
                        <Button
                            asChild
                            variant="outline"
                            size="lg"
                            data-testid="home-view-history-button"
                        >
                            <Link to="/history">View history</Link>
                        </Button>
                        <Button
                            asChild
                            variant="ghost"
                            size="lg"
                            data-testid="home-all-sessions-button"
                        >
                            <Link to="/all-sessions">
                                <Users className="h-4 w-4" />
                                Browse peers
                            </Link>
                        </Button>
                    </div>
                </section>

                {/* Progress card */}
                <section>
                    <Card className="border-border/80 shadow-sm" data-testid="home-progress-card">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                            <div className="flex items-center gap-2">
                                <TrendingUp className="h-4 w-4 text-primary" />
                                <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                                    Your progress
                                </CardTitle>
                            </div>
                            <Button asChild variant="ghost" size="sm" data-testid="home-profile-link">
                                <Link to="/profile">
                                    Full profile
                                    <ArrowRight className="h-3 w-3" />
                                </Link>
                            </Button>
                        </CardHeader>
                        <CardContent className="grid gap-6 md:grid-cols-[auto_1fr] md:items-center">
                            {!stats ? (
                                <div className="grid grid-cols-3 gap-6">
                                    <Skeleton className="h-12 w-20" />
                                    <Skeleton className="h-12 w-20" />
                                    <Skeleton className="h-12 w-20" />
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-3 gap-6">
                                        <Stat
                                            label="Sessions"
                                            value={stats.total_sessions ?? 0}
                                            testId="home-stat-sessions"
                                        />
                                        <Stat
                                            label="Practice time"
                                            value={`${stats.total_hours_practiced ?? 0}h`}
                                            testId="home-stat-hours"
                                        />
                                        <Stat
                                            label="Avg. score"
                                            value={stats.average_score != null ? Math.round(stats.average_score) : "—"}
                                            testId="home-stat-avg-score"
                                        />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                            Score trend
                                        </p>
                                        <Sparkline
                                            values={stats.recent_scores || []}
                                            height={64}
                                            dataTestId="home-sparkline"
                                        />
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </section>

                {/* Recent sessions */}
                <section>
                    <div className="mb-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <History className="h-4 w-4 text-primary" />
                            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                                Recent sessions
                            </h2>
                        </div>
                        {recent && recent.length > 0 && (
                            <Button asChild variant="ghost" size="sm" data-testid="home-view-all-link">
                                <Link to="/history">
                                    See all
                                    <ArrowRight className="h-3 w-3" />
                                </Link>
                            </Button>
                        )}
                    </div>
                    {!recent ? (
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                            <Skeleton className="h-72 rounded-2xl" />
                            <Skeleton className="h-72 rounded-2xl" />
                            <Skeleton className="h-72 rounded-2xl" />
                        </div>
                    ) : recent.length === 0 ? (
                        <EmptyState
                            icon={MessageSquare}
                            title="No sessions yet"
                            description="Start your first practice to see it here. Reviews and SOAP notes will appear after each session."
                            action={
                                <Button
                                    asChild
                                    variant="secondary"
                                    size="sm"
                                    data-testid="home-empty-start-button"
                                >
                                    <Link to="/practice/setup">
                                        Start your first practice
                                        <ArrowRight className="h-3 w-3" />
                                    </Link>
                                </Button>
                            }
                        />
                    ) : (
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="home-recent-grid">
                            {recent.map((s) => (
                                <SessionCard key={s.id} session={s} />
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </Layout>
    );
}

const Stat = ({ label, value, testId }) => (
    <div className="space-y-1" data-testid={testId}>
        <div className="text-2xl font-semibold tracking-tight text-foreground">{value}</div>
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {label}
        </div>
    </div>
);
